import React, { useState, useEffect, useRef } from 'react';
import { 
  ExpressCheckoutElement,
  useStripe, 
  useElements 
} from '@stripe/react-stripe-js';
import './style.css';
import { mjDebugLog, mjDebugError } from '../../utils/logger';
import { PAYMENT_TYPE_STRIPE } from '../../const/Pay';

/**
 * WalletPayButton Component
 * Provides Apple Pay and Google Pay integration using Stripe Express Checkout Element
 * 
 * @param {Object} props
 * @param {Object} props.price - Price object containing SKU and pricing information
 * @param {Function} props.onPaymentSuccess - Callback when payment succeeds
 * @param {Function} props.onPaymentError - Callback when payment fails
 * @param {Function} props.logEvent - Event logging function
 * @param {string} props.sessionId - Payment session ID
 */
const WalletPayButton = ({ 
  price, 
  onPaymentSuccess, 
  onPaymentError,
  logEvent = () => {},
  sessionId,
  onRenderStateChange = () => {}
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const containerRef = useRef(null);
  const renderStateResolvedRef = useRef(false);

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    // Check if wallet payment is available
    checkWalletAvailability();
  }, [stripe, elements]);

  const checkWalletAvailability = async () => {
    if (!stripe) {
      return;
    }

    try {
      // This will be handled by ExpressCheckoutElement internally
      // The element will automatically show/hide based on availability
      mjDebugLog('WalletPay initialized', {});
    } catch (error) {
      mjDebugError('WalletPay check failed', { error: error.message });
      logEvent('checkwalletpayerror_exposure', { 
        error: error.message 
      }, 'pay');
    }
  };

  const handleReady = (event) => {
    mjDebugLog('ExpressCheckout ready', { event });
    setIsReady(true);
    logEvent('expresscheckoutready_exposure', {}, 'pay');
  };

  const handleClick = (event) => {
    mjDebugLog('ExpressCheckout clicked', { 
      expressPaymentType: event.expressPaymentType 
    });
    
    // Resolve the event to proceed with payment
    event.resolve();
    
    logEvent('expresscheckoutclick_exposure', { 
      type: event.expressPaymentType || 'unknown'
    }, 'pay');
  };

  const handleConfirm = async (event) => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    mjDebugLog('ExpressCheckout confirm', { event });
    
    logEvent('expresscheckoutconfirm_exposure', {
      type: event.expressPaymentType || 'unknown'
    }, 'pay');

    try {
      // Submit the payment elements
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        mjDebugError('ExpressCheckout submit error', { 
          error: submitError.message 
        });
        
        logEvent('expresscheckoutsubmiterror_exposure', {
          error: submitError.message
        }, 'pay');
        
        if (onPaymentError) {
          onPaymentError(submitError.message);
        }
        
        setIsProcessing(false);
        return;
      }

      // Payment will be confirmed in parent component with client secret
      logEvent('wallet_click', { 
        sku: price?.sku?.stripe 
      }, 'pay');
      
      if (onPaymentSuccess) {
        onPaymentSuccess({
          paymentType: PAYMENT_TYPE_STRIPE,
          isWallet: true
        });
      }
      
    } catch (error) {
      mjDebugError('ExpressCheckout processing error', { 
        error: error.message 
      });
      
      logEvent('expresscheckoutelementerror_exposure', {
        error: error.message
      }, 'pay');
      
      if (onPaymentError) {
        onPaymentError('Payment processing error occurred.');
      }
      
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    mjDebugLog('ExpressCheckout cancelled', {});
    setIsProcessing(false);
    
    logEvent('expresscheckoutcancel_exposure', {}, 'pay');
  };

  const hasVisibleWalletUi = () => {
    const container = containerRef.current;
    if (!container) {
      return false;
    }

    const isVisibleNode = (node) => {
      if (!node) {
        return false;
      }

      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        rect.width > 16 &&
        rect.height > 16
      );
    };

    const iframeNodes = Array.from(container.querySelectorAll('iframe'));
    if (iframeNodes.some(isVisibleNode)) {
      return true;
    }

    const stripeHostNodes = Array.from(
      container.querySelectorAll('.__PrivateStripeElement')
    );
    return stripeHostNodes.some(isVisibleNode);
  };

  useEffect(() => {
    if (!isReady || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    let mutationObserver;
    let resizeObserver;
    let intervalId;

    const resolveRenderState = (visible) => {
      if (cancelled || renderStateResolvedRef.current) {
        return;
      }

      renderStateResolvedRef.current = true;
      onRenderStateChange(visible);

      if (!visible) {
        mjDebugLog('ExpressCheckout rendered without visible wallet UI', {});
      }
    };

    const checkRenderedState = () => {
      if (hasVisibleWalletUi()) {
        resolveRenderState(true);
        return true;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        resolveRenderState(false);
        return true;
      }

      return false;
    };

    mutationObserver = new MutationObserver(() => {
      if (checkRenderedState()) {
        mutationObserver.disconnect();
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        clearInterval(intervalId);
      }
    });
    mutationObserver.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (checkRenderedState()) {
          mutationObserver.disconnect();
          resizeObserver.disconnect();
          clearInterval(intervalId);
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    intervalId = window.setInterval(() => {
      if (checkRenderedState()) {
        mutationObserver.disconnect();
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        clearInterval(intervalId);
      }
    }, 200);

    checkRenderedState();

    return () => {
      cancelled = true;
      mutationObserver.disconnect();
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearInterval(intervalId);
    };
  }, [isReady, onRenderStateChange]);

  if (!stripe || !elements) {
    return null;
  }

  return (
    <div className="wallet-pay-container" ref={containerRef}>
      <div className="wallet-pay-loading" style={{ display: isReady ? 'none' : 'block' }}>
        <div className="wallet-pay-spinner"></div>
      </div>
      
      <ExpressCheckoutElement
        onReady={handleReady}
        onClick={handleClick}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        options={{
          buttonHeight: 48,
          buttonTheme: {
            applePay: 'black',
            googlePay: 'black',
          },
          buttonType: {
            applePay: 'buy',
            googlePay: 'buy',
          },
          paymentMethods: {
            applePay: 'auto',
            googlePay: 'auto',
            link: 'never',
          },
          layout: {
            maxColumns: 1,
            maxRows: 3,
          },
        }}
      />
    </div>
  );
};

export default WalletPayButton;
